import React, { useState } from "react";
import axios from "axios";
import { baseUrl } from "../../utils/Const";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (email) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous messages
    toast.dismiss();
    
    // Validate email
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    if (!validateEmail(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${baseUrl}forgot-password`, { email });
      console.log(response);
      
      // Show success message
      toast.success(
        response.data?.message || "Password reset link has been sent to your email. Please check your inbox.",
        {
          position: "top-right",
          autoClose: 6000,
        }
      );
      
      // Clear the form
      setEmail("");
      
    } catch (error) {
      console.error("Error:", error);
      
      // Handle different error cases
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.status === 404) {
        toast.error("Email not found. Please check if you entered the correct email.");
      } else if (error.response?.status === 429) {
        toast.error("Too many attempts. Please try again after some time.");
      } else {
        toast.error("Failed to send password reset link. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputFocus = () => {
    toast.dismiss();
  };

  return (
    <div className="img2">
      <ToastContainer />
      <div className="flex justify-center z-[1] items-center min-h-screen py-8">
        <div className="backdrop-blur-[2px]">
          
          <form
            onSubmit={handleSubmit}
            className="bg-gradient-to-br from-[#FFF8F0] to-[#F8F3E9] border-2 border-[#E6B17E] shadow-2xl rounded-2xl px-8 py-8 max-w-md w-full"
          >
            {/* Logo/Title Section */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#D35400] to-[#E67E22] rounded-full mb-4 shadow-lg">
                <span className="text-3xl text-white font-bold">CR</span>
              </div>
              <p className="text-3xl font-bold bg-gradient-to-r from-[#D35400] to-[#E67E22] bg-clip-text text-transparent">
                CloudRasoi
              </p>
              <p className="text-sm text-[#7E6B5A] mt-2">Restaurant Management System</p>
            </div>

            <h2 className="text-2xl font-bold mb-2 text-center text-[#2C3E50] border-b-2 border-[#E6B17E] pb-3">
              Reset Your Password
            </h2>
            
            <p className="text-[#7E6B5A] mb-8 text-center">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            {/* Email Input */}
            <div className="mb-8">
              <label className="block text-[#5D4037] text-sm font-semibold mb-3">
                Email Address <span className="text-[#D32F2F]">*</span>
              </label>
              <input
                type="email"
                name="email"
                placeholder="Enter your registered email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={handleInputFocus}
                className="w-full p-3 rounded-lg border-2 border-[#D7CCC8] bg-white text-[#5D4037] placeholder-[#A1887F] focus:outline-none focus:border-[#E67E22] focus:ring-2 focus:ring-[#E67E22]/30 transition-all duration-300"
              />
              <p className="text-[#7E6B5A] text-xs mt-2">
                Enter the email address associated with your CloudRasoi account
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-[#D35400] to-[#E67E22] hover:from-[#E67E22] hover:to-[#D35400] text-white font-bold py-3.5 px-4 rounded-lg shadow-lg hover:shadow-xl transform transition-all duration-300 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[#E67E22] focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending Reset Link...
                </span>
              ) : (
                'Send Reset Link'
              )}
            </button>

            {/* Back to Login */}
            <div className="mt-8 pt-6 border-t border-[#D7CCC8]">
              <div className="text-center">
                <p className="text-[#7E6B5A]">
                  Remember your password?{" "}
                  <Link
                    to="/"
                    className="font-bold text-[#D35400] hover:text-[#E67E22] hover:underline underline-offset-2 transition-colors duration-300"
                  >
                    Back to Login
                  </Link>
                </p>
              </div>
            </div>

            {/* Security Note */}
            <div className="mt-6 p-4 bg-gradient-to-r from-[#E8F5E9] to-[#F1F8E9] border-l-4 border-[#4CAF50] rounded">
              <p className="text-[#2E7D32] text-sm">
                <span className="font-semibold">Security Note:</span> The password reset link will be valid for 1 hour. For security reasons, please don't share this link with anyone.
              </p>
            </div>

            {/* Additional Help */}
            <div className="mt-4 text-center">
              <p className="text-xs text-[#A1887F]">
                Need help?{" "}
                <Link to="/support" className="text-[#D35400] hover:underline">
                  Contact Support
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
